// src/pages/clab-selection-requests/components/RejectModal.jsx
import React from 'react';
import Icon from '../../../components/AppIcon';

/**
 * Reusable reject modal component with form
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Object} props.application - Selected application
 * @param {Array} props.rejectionReasons - Available rejection reasons
 * @param {Object} props.formData - Form data object
 * @param {boolean} props.processing - Whether rejection is in progress
 * @param {Function} props.onFormChange - Form change callback
 * @param {Function} props.onConfirm - Confirm rejection callback
 * @param {Function} props.onCancel - Cancel rejection callback
 */
const RejectModal = ({ 
  show, 
  application, 
  rejectionReasons, 
  formData, 
  processing, 
  onFormChange, 
  onConfirm, 
  onCancel 
}) => {
  if (!show) return null;

  const handleReasonChange = (e) => {
    onFormChange({ reason_id: e.target.value });
  };

  const handleRemarksChange = (e) => {
    onFormChange({ remarks: e.target.value });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
            <Icon name="XCircle" className="h-6 w-6 text-red-600" />
          </div>
          
          <div className="mt-4 text-center">
            <h3 className="text-lg font-medium text-gray-900">
              Reject Application
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              Please provide a reason for rejecting this application.
            </p>
            
            {application && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md">
                <p className="text-sm font-medium text-gray-900">
                  {application.employer?.company_name}
                </p>
                <p className="text-sm text-gray-600">
                  Application ID: {application.id}
                </p>
              </div>
            )}
          </div>
          
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason *
              </label>
              <select
                value={formData.reason_id}
                onChange={handleReasonChange}
                disabled={processing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                required
              >
                <option value="">Select a reason</option>
                {rejectionReasons.map((reason) => (
                  <option key={reason.id} value={reason.id}>
                    {reason.reason}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Remarks
              </label>
              <textarea
                value={formData.remarks}
                onChange={handleRemarksChange}
                disabled={processing}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                placeholder="Optional additional comments..."
              />
            </div>
          </div>
          
          <div className="flex justify-center space-x-3 mt-6">
            <button
              onClick={onCancel}
              disabled={processing}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={processing || !formData.reason_id}
              className="px-4 py-2 bg-red-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {processing && (
                <Icon name="Loader2" className="h-4 w-4 mr-2 animate-spin" />
              )}
              {processing ? "Rejecting..." : "Reject"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectModal;